import React, { useMemo, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Controller, useWatch } from 'react-hook-form'
import {
  Grid,
  TextField,
  Typography,
  DatePicker,
  FormLabel,
} from '@the-platform-group/component-library'
import { AsyncSelect } from '@the-platform-group/component-library/Select'
import { debouncedLoadCustomers, debouncedLoadCustomerUsers } from 'utils/optionsLoaders'
import SecuritySelect from 'components/Select/SecuritySelect'
import UploadedFilesDisplay from '../../components/UploadedFilesDisplay'
import { getCustomer } from 'services/customers'

/**
 * Form component for client requests (used for both creating and editing)
 * @param {object} props - component props
 * @param {object} props.control - react-hook-form control object
 * @param {object} props.formState - react-hook-form formState object
 * @param {function} props.setValue - react-hook-form setValue function
 * @param {Array} props.uploadedFiles - array of uploaded files
 * @param {Array} props.pendingFiles - array of files currently being uploaded
 * @param {function} props.onFileRemove - function to remove uploaded files
 * @returns {React.ReactElement} The rendered RequestForm component
 */
const RequestForm = ({
  control,
  formState,
  setValue,
  uploadedFiles = [],
  pendingFiles = [],
  onFileRemove,
  request = null,
  isEdit = false,
}) => {
  const { errors } = formState

  // Watch the selected customer to enable user selection
  const selectedCustomer = useWatch({ control, name: 'customerName' })

  const [customerSecurityData, setCustomerSecurityData] = useState(null)
  const isMultiSecurity = customerSecurityData?.features?.includes('multi_security')
  const defaultSecurity = customerSecurityData?.defaultSecurity

  // Effect to fetch customer security data when customer changes
  useEffect(() => {
    const fetchCustomerSecurityData = async () => {
      if (!selectedCustomer?.id) {
        setCustomerSecurityData(null)
        setValue('security', null)
        return
      }

      try {
        const customerData = await getCustomer(selectedCustomer.id)
        setCustomerSecurityData(customerData.data)

        // Set default security if available
        if (customerData.data?.defaultSecurity) {
          setValue('security', customerData.data.defaultSecurity)
        } else {
          setValue('security', null)
        }
      } catch (error) {
        console.error('Error fetching customer data:', error)
        setCustomerSecurityData(null)
        setValue('security', null)
      }
    }

    fetchCustomerSecurityData()
  }, [selectedCustomer?.id, setValue])

  const loadCustomers = useMemo(() => {
    return term => debouncedLoadCustomers({ term, state: 'active' })
  }, [])
  const loadCustomerUsers = useMemo(() => {
    return term =>
      debouncedLoadCustomerUsers({
        customerIds: [selectedCustomer?.id],
        activeUsersOnly: true,
        nameTerm: term,
        pageSize: 50,
        pageOffset: 0,
        excludeAssistantUsers: true,
      })
  }, [selectedCustomer?.id])

  return (
    <>
      <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
        {isEdit
          ? `Editing a request on behalf of a client allows you to include essential details needed to fulfill the request.`
          : `Create a request on behalf of a client. Make sure all relevant details are included, such as
        specific instructions (e.g. formatting, presentation) and any supporting documents (e.g.
        PDFs, spreadsheets) for complete documentation.`}
      </Typography>
      <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
        {isEdit
          ? `Please note: Any changes made to the request will appear in the customer's account. Be sure to review and confirm the accuracy of the information before proceeding.`
          : `Please note: This request will appear in the respective customer account. Please review and
        confirm that the information is accurate before proceeding.`}
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormLabel htmlFor="customerName">Customer Name</FormLabel>
          <Controller
            name="customerName"
            control={control}
            rules={{ required: 'Customer Name is required' }}
            render={({ field }) => (
              <AsyncSelect
                {...field}
                onChange={value => {
                  field.onChange(value)
                  // Clear the requestedBy field when customer changes
                  setValue('requestedBy', null)
                  // Security field will be handled automatically via useEffect when customer data changes
                }}
                id="customerName"
                variant="outlined"
                fullWidth
                hideArrow
                includeClearAll
                placeholder="Select customer"
                optionsLoader={loadCustomers}
                {...(errors.customerName && { error: true })}
                helperText={errors.customerName?.message}
                margin="none"
                sx={{ mt: 1 }}
                disabled={isEdit && !request?.adminCreated} // Disable if editing and not admin created
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <FormLabel htmlFor="requestedBy">Requested By</FormLabel>
          <Controller
            name="requestedBy"
            control={control}
            rules={{ required: 'Requested By is required' }}
            render={({ field }) => (
              <AsyncSelect
                {...field}
                id="requestedBy"
                variant="outlined"
                fullWidth
                hideArrow
                includeClearAll
                placeholder="Select user"
                optionsLoader={loadCustomerUsers}
                disabled={!selectedCustomer || (isEdit && !request?.adminCreated)} // Disable if no customer selected or editing and not admin created
                {...(errors.requestedBy && { error: true })}
                helperText={
                  errors.requestedBy?.message ||
                  (!selectedCustomer ? 'Please select a customer first' : '')
                }
                margin="none"
                sx={{ mt: 1 }}
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <FormLabel htmlFor="requestDate">Request Date</FormLabel>
          <Controller
            name="requestDate"
            control={control}
            rules={{ required: 'Request Date is required' }}
            render={({ field }) => (
              <DatePicker
                {...field}
                id="requestDate"
                variant="outlined"
                fullWidth
                {...(errors.requestDate && { error: true })}
                helperText={errors.requestDate?.message}
                placeholder="Request date"
                disabled={isEdit && !request?.adminCreated} // Disable if editing and not admin created
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <FormLabel htmlFor="security">Security (Optional)</FormLabel>
          <Controller
            name="security"
            control={control}
            render={({ field: { ref, ...field } }) => (
              <SecuritySelect
                {...field}
                id="security"
                placeholder="Select security"
                {...(errors.security && { error: true })}
                disabled={!isMultiSecurity && defaultSecurity} // Read-only for single-security with default
                helperText={
                  !isMultiSecurity && defaultSecurity ? 'Single Security Customer' : undefined
                }
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <FormLabel htmlFor="requestName">Request Name</FormLabel>
          <Controller
            name="requestName"
            control={control}
            rules={{ required: 'Request Name is required' }}
            render={({ field }) => (
              <TextField
                {...field}
                id="requestName"
                fullWidth
                {...(errors.requestName && { error: true })}
                helperText={errors.requestName?.message}
                placeholder="Name"
                sx={{ mt: 1 }}
                disabled={isEdit && !request?.adminCreated} // Disable if editing and not admin created
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <FormLabel htmlFor="description">Description</FormLabel>
          <Controller
            name="description"
            control={control}
            rules={{ required: 'Description is required' }}
            render={({ field }) => (
              <TextField
                {...field}
                id="description"
                fullWidth
                multiline
                rows={4}
                {...(errors.description && { error: true })}
                helperText={errors.description?.message}
                placeholder="Describe your upload request and attach your file, if any."
                sx={{ mt: 1 }}
              />
            )}
          />
        </Grid>

        {/* Uploaded Files Display */}
        {(uploadedFiles.length > 0 || pendingFiles.length > 0) && (
          <Grid item xs={12} sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <UploadedFilesDisplay
              uploadedFiles={uploadedFiles}
              pendingFiles={pendingFiles}
              onFileRemove={onFileRemove}
            />
          </Grid>
        )}
      </Grid>
    </>
  )
}

RequestForm.propTypes = {
  control: PropTypes.object.isRequired,
  formState: PropTypes.object.isRequired,
  setValue: PropTypes.func.isRequired,
  uploadedFiles: PropTypes.array,
  pendingFiles: PropTypes.array,
  onFileRemove: PropTypes.func,
  request: PropTypes.object,
  isEdit: PropTypes.bool,
}

export default RequestForm
